// dose-log.js — the single source of truth for the nurse's deliberate dose-logging
// grammar (contract §11b). The standalone Worklist screen (Worklist.jsx), the ward
// board's inline worklist lane (WardBoard.jsx), the round session (RoundRun.jsx),
// the cardex PRN give and the command dock's log_dose action (lib/surface-actions.js)
// all import these, so the outcome set, the reason menus and — critically — the FHIR
// write are byte-identical across every surface. The surfaces differ ONLY in
// presentation (Worklist uses the Calm-Clinical token set; WardBoard uses the
// theme-aware shadcn tokens); the write path lives here, once.
//
// It lives in lib/ (moved from screens/ward/ 2026-08-05) because it is pure logic +
// vocabulary with no JSX and no DOM: lib/surface-actions.js needs it, and lib must
// never import upward from screens/.
import { recordDoseTick } from './ipd-live.js';

// The three exception outcomes offered beside the primary "Given" confirm.
export const HOLD_OUTCOMES = [
  { key: 'held', label: 'Hold', hi: 'रोकें' },
  { key: 'refused', label: 'Refused', hi: 'मना किया' },
  { key: 'unavailable', label: 'Unavailable', hi: 'उपलब्ध नहीं' },
];

// Per-outcome quick reason chips (tap-to-save).
export const HOLD_REASONS = {
  held: ['NBM', 'Pre-procedure', 'Doctor advised', 'Vitals concern'],
  refused: ['Patient refused', 'Nausea', 'Asleep'],
  unavailable: ['Out of stock', 'Pharmacy pending'],
};

export const OUTCOME_LABEL = { given: 'Given', held: 'Held', refused: 'Refused', unavailable: 'Unavailable' };

// The write — FHIR-direct as the logged-in user. The row's EXACT slotKey keys a
// scheduled dose so the due-engine marks THIS slot done (wave-1 lesson: never
// recompute the slotKey). A PRN give mints a distinct `dose:{id}:prn:{ISO}` key at
// tap time so each PRN administration is its own record. Fail loud — the caller
// keeps the panel open and shows the message on rejection; the tick is never lost.
//
// `adjustedDoseText` (2026-07-26) is the "this dose only" override recordDoseTick
// has always accepted: the administration records the dose ACTUALLY given and the
// order stays exactly as prescribed (contract §7b). It was missing from this
// signature, so every ⋯-panel adjusted dose was silently dropped on the way to the
// write — additive fix, no existing caller changes behaviour.
export async function commitDoseTick(item, { outcome, reason, note, adjustedDoseText, aiOrigin } = {}) {
  const o = item.order || {};
  const slotKey = item.prn ? `dose:${o.id}:prn:${new Date().toISOString()}` : item.slotKey;
  const trimmed = (note || '').trim();
  return recordDoseTick({
    order: o,
    encounterId: item.encounterId,
    patientId: item.patient && item.patient.id,
    slotKey,
    outcome,
    ...(reason ? { reason } : {}),
    ...(trimmed ? { note: trimmed } : {}),
    ...(adjustedDoseText && String(adjustedDoseText).trim() ? { adjustedDoseText: String(adjustedDoseText).trim() } : {}),
    // AI-origin provenance (DEFERRED.md → AI-origin): when the dock's command agent
    // logs a dose from voice/text, the verbatim utterance rides as the sourceQuote so
    // the MedicationAdministration is auditable as AI-originated. Manual taps omit it.
    ...(aiOrigin ? { aiOrigin } : {}),
  });
}
